<template>
  <section style="width:100%">
    <div style="position: relative;">
      <base-multiple-images
        type="u"
        image-type="banner"
        :profile-id="documentById.profile.profileId.replace('#', '')"
        :file-names="documentById.document.template[0].fields[3].value.names"
        :contain="false"
        :image-props="{ contain: false }"
        :aspectRatio="25 / 9"
        useLazy
        :imageAlt="`${$t('showcase')} ${documentName} ${$t('bannerImage')}.`"
      ></base-multiple-images>
    </div>

    <v-col class="mt-3 pa-0">
      <div class="d-flex align-center justify-space-between">
        <div class="d-inline-block">
          <component
            :is="$vuetify.breakpoint.mdAndUp ? 'h2' : 'h3'"
            class="mr-2"
          >
            {{ documentName }}
          </component>
        </div>

        <!-- Menu Report Abuse -->
        <div class="d-flex align-center">
          <share-menu
            v-if="isPublishedDocument"
            @close="showDocumentShare = false"
            entity="DOCUMENT"
            :show="showDocumentShare"
            :entity-id="documentById.document.documentId.replace('#', '')"
            :description="stripHtml(documentDescription)"
            :title="documentName"
            :projectHashtags="projectHashtags"
          >
            <template v-slot:activator>
              <v-btn @click="showDocumentShare = true" icon class="white mr-1">
                <v-icon color="primary" small>{{
                  icons.mdiShareVariant
                }}</v-icon>
              </v-btn>
            </template>
          </share-menu>

          <v-menu v-if="!showReportMenu" offset-y>
            <template v-slot:activator="{ on }">
              <v-btn color="primary" dark icon v-on.stop="on" small>
                <v-icon>{{ icons.mdiDotsVertical }}</v-icon>
              </v-btn>
            </template>
            <v-list>
              <v-list-item @click.stop="showReportAbuse = true">
                <v-list-item-content>
                  <v-row class="ma-0" align="center">
                    <v-icon color="primary" class="mr-2">{{
                      icons.mdiAlertOctagon
                    }}</v-icon>
                    {{ $t('reportAbuse') }}
                  </v-row>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
      </div>

      <span class="subtitle-1 grey--text">
        {{ documentSlogan }}
      </span>

      <div
        :style="
          !descSeeMore && shouldTruncate
            ? 'height: 100px; overflow: hidden'
            : ''
        "
      >
        <div
          ref="truncator"
          class="text-description"
          style="color: #9e9e9e; font-size: .875rem"
          v-html="documentDescription"
        ></div>
      </div>

      <span
        v-if="showMoreDescriptionOption && shouldTruncate"
        @click="descSeeMore = !descSeeMore"
        class="info--text cursor-pointer mb-2"
        style="font-size : .75rem;"
        >{{ descSeeMore ? $t('less') : $t('more') }}
        <v-icon color="info" small>{{
          descSeeMore ? icons.mdiChevronUp : icons.mdiChevronDown
        }}</v-icon>
      </span>

      <v-col class="mt-3 pa-0">
        <base-showcase-tabs
          title="learnMore"
          :document="documentById.document"
          :profile-info="{ profile: { ...documentById.profile } }"
          :key="`showcase-tabs-${documentKey}`"
          :documentKey="`showcase-tabs-${documentKey}`"
        ></base-showcase-tabs>
      </v-col>
    </v-col>

    <base-report-abuse
      v-if="showReportAbuse"
      :show="showReportAbuse"
      :entityType="entityType"
      :entityName="documentById.document.name"
      :entityId="documentById.document.documentId"
      @close="showReportAbuse = false"
    ></base-report-abuse>
  </section>
</template>

<script>
import { mapState } from 'vuex';

import { TemplateArrayHelpers } from '@/utilities/mixins/TemplateArrayHelpers';

import BaseMultipleImages from '@/components/base_components/BaseMultipleImages/BaseMultipleImages.vue';
import ShareMenu from '@/components/base_components/ShareMenu/ShareMenu.vue';
import BaseShowcaseTabs from '@/components/base_components/BaseShowcaseTabs/BaseShowcaseTabs.vue';

import camelCase from 'lodash/camelCase';
import {
  mdiDotsVertical,
  mdiAlertOctagon,
  mdiShareVariant,
  mdiChevronDown,
  mdiChevronUp
} from '@mdi/js';

const BaseReportAbuse = () =>
  import('@/components/base_components/BaseReportAbuse/BaseReportAbuse.vue');

export default {
  components: {
    BaseShowcaseTabs,
    ShareMenu,
    BaseReportAbuse,
    BaseMultipleImages
  },

  props: {
    documentById: {
      type: Object,
      required: true
    },

    documentKey: {
      type: String,
      required: false,
      default: 'showcase-tabs-'
    },

    projectHashtags: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      showDocumentShare: false,
      descSeeMore: false,
      showMoreDescriptionOption: false,
      shouldTruncate: true,
      showReportAbuse: false
    };
  },

  mounted() {
    const descriptionLength = this.documentDescription.length;

    if (descriptionLength > 305) this.showMoreDescriptionOption = true;
    if (this.$refs.truncator?.clientHeight > 100) {
      this.shouldTruncate = true;
    } else {
      this.shouldTruncate = false;
    }

    this.addBlankTarget();
  },

  mixins: [TemplateArrayHelpers],

  methods: {
    stripHtml(html) {
      const tmp = document.createElement('DIV');
      tmp.innerHTML = html;
      return tmp.textContent || tmp.innerText || '';
    },

    addBlankTarget() {
      const descriptionElement = document.getElementsByClassName(
        'text-description'
      );

      descriptionElement.forEach(parent => {
        const aChildNodes = parent.getElementsByTagName('a');
        if (aChildNodes.length > 0) {
          aChildNodes.forEach(node => {
            node.setAttribute('target', '_blank');

            const att = node.getAttribute('href');
            if (!att.includes('http'))
              node.setAttribute('href', `http://${att}`);
          });
        }
      });
    }
  },

  computed: {
    ...mapState({
      userProfile: state => state.authentication.userProfile,
      defaultLanguage: state => state.defaultLanguage
    }),

    icons: () => ({
      mdiDotsVertical,
      mdiAlertOctagon,
      mdiShareVariant,
      mdiChevronDown,
      mdiChevronUp
    }),

    showReportMenu() {
      if (!this.userProfile?.profile) return false;

      return (
        this.userProfile?.profile?.profileId ===
        this.documentById?.profile?.profileId
      );
    },

    entityType() {
      if (!this.documentById?.document) return '';

      return this.documentById?.document?.creativity
        ? 'CREATIVITY'
        : 'DOCUMENT';
    },

    documentDefaultLang() {
      return this.documentById?.document?.defaultLanguage;
    },

    documentName() {
      return this.findTranslationMultipleOptions(
        this.documentById.document.template[0].fields[0].value,
        this.documentDefaultLang,
        this.defaultLanguage
      );
    },

    documentSlogan() {
      return this.findTranslationMultipleOptions(
        this.documentById.document.template[0].fields[1].value,
        this.documentDefaultLang,
        this.defaultLanguage
      );
    },

    documentDescription() {
      return this.findTranslationMultipleOptions(
        this.documentById.document.template[0].fields[2].value,
        this.documentDefaultLang,
        this.defaultLanguage
      );
    },

    isPublishedDocument() {
      return this.documentById.document.status === 'PUBLISHED';
    }
  }
};
</script>

<style scoped>
.clamp-text {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  overflow: hidden;
}
</style>
